import { AssociationSource } from "common/associations/association-browser.service";
import { Group } from "common/associations/association-group.service";
import { Association } from "common/associations/association-list.component";
import { AssociationStore } from "common/associations/association-store.interface";
import { AssocSearchResult } from "common/associations/search-query.logic";
import { ProcessElementsResource } from "core/stages-client";
import { ViewService } from "core/view.service";
import { keyBy } from "lodash";
import { ProcessComplianceAssociationService } from "process/compliance/associations/process-compliance-association.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

type ProcessTreeItem = stages.process.ProcessTreeItem;
type ProcessComplianceAssociationGroup = stages.compliance.ProcessComplianceAssociationGroup;
type ProcessView = stages.process.ProcessView;
type ViewableProcess = stages.process.ViewableProcess;
type ViewableType = stages.process.ViewableType;

type TargetElement = stages.compliance.TargetElement;

export class ProcessComplianceAdapter implements AssociationSource<TargetElement> {
	constructor(private element: ProcessView) {}

	get associations(): Record<string, stages.compliance.ProcessComplianceAssociationGroup> {
		return keyBy(this.element.processComplianceContainer, "groupKey");
	}

	updateAssociations(groupKey: string, processComplianceContainer: ProcessComplianceAssociationGroup[]): void {
		const groupToUpdate = this.element.processComplianceContainer!.find((g) => g.groupKey === groupKey);
		const newGroup = processComplianceContainer.find((g) => g.groupKey === groupKey);
		if (groupToUpdate && newGroup) {
			groupToUpdate.list = newGroup.list;
		}
	}

	get parent(): ProcessTreeItem | undefined {
		return this.element.parent;
	}

	get process(): ViewableProcess {
		return this.element.process;
	}

	get type(): ViewableType {
		return this.element.type;
	}

	get id(): string {
		return this.element.id;
	}

	get identity(): string {
		return this.element.identity;
	}

	get dependentElementsContainer(): boolean {
		return !!this.element.dependentElementsContainer;
	}
}

export class ProcessComplianceAssociationStore implements AssociationStore<ProcessComplianceAdapter, TargetElement> {
	constructor(
		private viewService: ViewService,
		private associationService: ProcessComplianceAssociationService,
		private readonly processElementsResource: ProcessElementsResource,
	) {}

	getAddMenuEntryNameKey(_group: Group<TargetElement>): string {
		return "compliance.references.addForProcess";
	}

	getAddMenuEntryNameKeyParam(_group: Group<TargetElement>): string | undefined {
		return undefined;
	}

	getAddMenuEntryNameKeyParamValue(group: Group<TargetElement>): string | undefined {
		return group.name;
	}

	getInputClasses(
		typeIdent: string | undefined,
		isDependentElement: boolean,
		subtypeIdent: string | null,
		additionalClasses?: string | undefined,
	): string[] {
		if (typeIdent) {
			// Icon for search results dropdown items
			return this.viewService.getIconClasses(
				{
					ident: typeIdent,
					subtypeIdent: subtypeIdent,
				},
				false,
				isDependentElement,
				additionalClasses,
			);
		}
		// Icon for search input text box
		return ["ico", "ico-compliance"];
	}

	getIconClasses(
		targetElement: TargetElement,
		isDependentElement: boolean,
		additionalClasses?: string | undefined,
	): string[] {
		return this.viewService.getIconClasses(
			targetElement.type,
			targetElement.index,
			isDependentElement,
			additionalClasses,
		);
	}

	getLabelClasses(
		_sourceElement: ProcessComplianceAdapter,
		targetElement?: TargetElement,
		_additionalClasses?: string | undefined,
	): string[] {
		return targetElement?.tailored ? ["tailored"] : [];
	}

	getQuickAssignLabelClasses(searchResult: AssocSearchResult, _additionalClasses?: string | undefined): string[] {
		return searchResult.tailored ? ["tailored"] : [];
	}

	getActions(association: Association<TargetElement>): StringToBoolean {
		return association.allowedOperations ? association.allowedOperations : {};
	}

	getTargetElementActions(targetElement: TargetElement): StringToBoolean {
		return targetElement.actions;
	}

	getSourceElement(
		_type: string,
		_identity: string,
		_workspaceId: string,
		_pv: string,
	): Observable<ProcessComplianceAdapter> {
		return this.viewService.awaitSelfElementObservable().pipe(map((self) => new ProcessComplianceAdapter(self)));
	}

	getAdditionalSourceElements(_sourceElement: ProcessComplianceAdapter): ProcessComplianceAdapter[] {
		return [];
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings! This comment was added as part of ST-32708: "Migrate from TSLint to ESLint".
	async getParentForNewTargetElement(
		_sourceElement: ProcessComplianceAdapter,
		_targetType: string,
	): Promise<TargetElement | null> {
		throw new Error("Method not implemented.");
	}

	asTypedId(sourceElement: ProcessComplianceAdapter): stages.core.TypedId {
		return {
			typeIdent: sourceElement.type.ident,
			id: sourceElement.id,
		};
	}

	// rule is currently disabled, because function throws always an error
	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings! This comment was added as part of ST-32708: "Migrate from TSLint to ESLint".
	async createAssociationToNewTargetElement(
		_sourceElement: stages.core.TypedId,
		_newElementParent: TargetElement,
		_targetType: string,
		_targetSubtype: string | null,
		_name: string,
		_associationType: string,
		_sourceRole: string | undefined,
		_workspaceId: string,
		_pv: string,
	): Promise<Association<TargetElement>> {
		throw new Error("Method not implemented.");
	}

	createElementAndChangeAssociationSource(
		_sourceElementParent: ProcessComplianceAdapter,
		_sourceType: string,
		_sourceSubtype: string,
		_name: string,
		_workspaceId: string,
		_pv: string,
		_dependent: boolean,
		_associationId: string,
		_targetElementType: string,
		_targetElementId: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	changeAssociationSourceOrTarget(
		_sourceElement: ProcessComplianceAdapter,
		_associationId: string,
		_sourceElementType: string,
		_sourceElementId: string,
		_targetElementType: string,
		_targetElementId: string,
		_workspaceId: string,
		_pv: string,
		_associationPath: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	createElementAndChangeAssociationTarget(
		_newElementParent: TargetElement,
		_targetType: string,
		_targetSubtype: string,
		_name: string,
		_workspaceId: string,
		_pv: string,
		_dependent: boolean,
		_associationId: string | undefined,
		_associationType: string,
		_sourceRole: string | undefined,
		_sourceElementType: string,
		_sourceElementId: string,
	): Promise<Association<TargetElement>> {
		throw new Error("not implemented");
	}

	async createAssociation(
		sourceElement: stages.core.TypedId,
		targetElementId: string,
		targetElementType: string,
		_associationType: string,
		_sourceRole: string | undefined,
		_currentWorkspaceId: string,
		_pv: string,
	): Promise<Association<TargetElement>> {
		const association = await this.associationService.createAssociation(
			sourceElement.id,
			sourceElement.typeIdent,
			targetElementId,
			targetElementType,
		);
		return association as Association<TargetElement>;
	}

	// rule is currently disabled, because function throws always an error
	// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings! This comment was added as part of ST-32708: "Migrate from TSLint to ESLint".
	async createCommentOnlyAssociation(
		_sourceElement: stages.core.TypedId,
		_targetType: string,
		_targetSubtype: string | null,
		_associationType: string,
		_sourceRole: string | undefined,
		_workspaceId: string,
		_pv: string,
		_comment: string,
	): Promise<Association<TargetElement>> {
		throw new Error("Method not implemented.");
	}

	async deleteAssociations(
		associationsToDelete: Association<TargetElement>[],
		workspaceId: string,
		pv: string,
	): Promise<Association<TargetElement>[]> {
		return this.associationService
			.deleteAssociations(
				associationsToDelete.map((assoc) => assoc.id),
				workspaceId,
				pv,
			)
			.then(() => associationsToDelete);
	}

	async updateComment(associationId: string, comment: string, currentWorkspaceId: string, pv: string): Promise<void> {
		return this.associationService.updateComment(associationId, comment, currentWorkspaceId, pv);
	}

	async searchProcessElements(
		searchTerm: string,
		_targetElementType: string,
		_targetElementSubtypes: string[],
		targetElementsToIgnore: TargetElement[],
		_currentWorkspaceId: string,
		searchWorkspaceId: string,
		pv: string,
	): Promise<AssocSearchResult[]> {
		return this.associationService.searchProcessElements(
			searchTerm,
			targetElementsToIgnore.map((e) => ({ typeIdent: e.type.ident, id: e.id })),
			searchWorkspaceId,
			pv,
		);
	}

	async searchDependentProcessElements(
		_searchTerm: string,
		_targetElementType: string,
		_containerElementId: string,
		_targetElementsToIgnore: TargetElement[],
		_currentWorkspaceId: string,
		_pv: string,
	): Promise<AssocSearchResult[]> {
		// eslint-disable-next-line unicorn/no-useless-promise-resolve-reject -- promise needed by interface
		return Promise.resolve([]);
	}

	async applyAssociationModifications(
		sourceElement: ProcessComplianceAdapter,
		groupPath: string,
		added: stages.core.TypedId[],
		removed: stages.core.TypedId[],
		modifiedCommentJson: stages.core.model.TypedIdAssociationDetails[],
		processModelWorkspaceId: string,
		pv: string,
		referenceModelWorkspaceId: string,
	): Promise<Association<TargetElement>[]> {
		const sourceElementAfterUpdate = await this.associationService.applyAssociationModifications(
			sourceElement.type.ident,
			sourceElement.id,
			groupPath,
			added,
			removed,
			modifiedCommentJson,
			processModelWorkspaceId,
			referenceModelWorkspaceId,
		);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings! This comment was added as part of ST-32708: "Migrate from TSLint to ESLint".
		const selfElement = this.viewService.getSelf(sourceElementAfterUpdate as any);
		const updatedAssocs = keyBy(selfElement.processComplianceContainer, "groupKey")[groupPath];
		sourceElement.updateAssociations(groupPath, selfElement.processComplianceContainer);
		this.viewService.notifyModified();
		return updatedAssocs;
	}

	getLink(association: Association<TargetElement>, pv: string): unknown[] {
		return [
			"/",
			"workspace",
			association.targetElement!.workspaceId,
			pv,
			"process",
			association.targetElement!.type.ident,
			association.targetElement!.identity,
		];
	}

	isSelectableInBrowser(
		existingAssociation: Association<TargetElement> | null,
		sourceElement: ProcessComplianceAdapter,
		targetElement: TargetElement,
		_targetSubtypes: string[] | undefined,
	): boolean {
		if (targetElement.type.ident === "process") {
			return false;
		}

		return !existingAssociation || this.getActions(existingAssociation).DELETE;
	}

	reset(): void {
		// not needed
	}

	async getTree(browseWorkspaceId: string, elementTypes: string[]): Promise<stages.process.TreeElement[]> {
		return this.processElementsResource.getTree(browseWorkspaceId, elementTypes[0], { elementTypes: elementTypes });
	}

	// eslint-disable-next-line @typescript-eslint/require-await -- not implemented
	async rearrangeAssociations(
		_currentWorkspaceId: string,
		_sortedAssociationIds: string[],
		_sortStrategy: stages.core.sort.SortStrategy,
		_type: string,
		_identity: string,
		_path: string,
		_groupBy: string,
		_pv: string,
	): Promise<Record<string, never>> {
		throw new Error("Method not implemented.");
	}

	getGroupIdentifier(groupPath: string, _groupBy?: string): string {
		return groupPath;
	}
}
